import { Formik, Form } from "formik";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo } from "react";
import { AdvocateSelector } from "@/core/components/advocate-selector";
import { PhoneInput } from "@/core/components/phone-input";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@volca/ui/components/ui/dialog";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { getErrorMessage } from "@/core/api/errors";
import { useBusiness } from "@/core/business";
import { normalizeSignupSource } from "@/core/utils/form";
import { trackReferralCreation } from "@/referrals/events";
import { useCreateReferral } from "@/referrals/query";
import { IReferral } from "@/referrals/referral";
import { useGetMyUser } from "@/user/query";
import { NewAdvocateFields } from "./new-advocate-fields";
import { initialValues, referralSchema } from "./schema";

interface AddReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (data: IReferral) => void;
  onError?: (error: Error) => void;
  trigger?: React.ReactElement;
  businessId?: string;
}

const AddReferralModal = ({
  open,
  onOpenChange,
  onSuccess,
  onError,
  trigger,
  businessId,
}: AddReferralModalProps) => {
  const posthog = usePostHog();

  const { businessId: userBusinessId } = useBusiness();

  const { data: userInfo } = useGetMyUser();

  const effectiveBusinessId = businessId || (userBusinessId ?? undefined);

  const { mutate: createReferral } = useCreateReferral(effectiveBusinessId);

  const memoizedInitialValues = useMemo(
    () => ({
      ...initialValues,
      signup_source_id: userInfo?.id || "none",
      signup_source_type: "User",
    }),
    [userInfo?.id],
  );

  const handleSubmit = useCallback(
    (values: typeof initialValues) => {
      const normalizedSignupSource = normalizeSignupSource(
        values.signup_source_id,
        values.signup_source_type,
      );

      const payload = {
        ...values,
        business_id: effectiveBusinessId || (userInfo?.parentable_id as string),
        advocate: values.isAddingNewAdvocate
          ? {
              name: values.advocate_name,
              phone_number: values.advocate_phone,
              ...normalizedSignupSource,
              onboard: values.onboard,
            }
          : undefined,
        advocate_id: !values.isAddingNewAdvocate
          ? values.advocate_id
          : undefined,
      };
      createReferral(payload, {
        onSuccess: (response: IReferral) => {
          trackReferralCreation(posthog, {
            success: true,
            data: values,
          });
          toast.success("Referral created successfully");
          onSuccess?.(response);
          onOpenChange(false);
        },
        onError: (error) => {
          trackReferralCreation(posthog, {
            success: false,
            data: values,
          });
          toast.error("Failed to create referral", {
            description: getErrorMessage(error),
          });
          onError?.(error);
        },
      });
    },
    [
      createReferral,
      effectiveBusinessId,
      userInfo?.parentable_id,
      onSuccess,
      onError,
      onOpenChange,
      posthog,
    ],
  );

  return (
    <Formik
      initialValues={memoizedInitialValues}
      validationSchema={referralSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
        resetForm();
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        getFieldProps,
        setFieldValue,
        isSubmitting,
        isValid,
      }) => (
        <Dialog
          open={open}
          onOpenChange={(open) => {
            if (!open) {
              setFieldValue("isAddingNewAdvocate", false);
            }
            onOpenChange(open);
          }}
        >
          {trigger && <DialogTrigger render={trigger} />}
          <DialogContent className="max-h-[80vh] min-h-0 grid-rows-[auto_1fr_auto] overflow-hidden">
            <Form className="contents h-full">
              <DialogHeader>
                <DialogTitle>New referral</DialogTitle>
                <DialogDescription className="sr-only">
                  Create a new referral for a potential customer.
                </DialogDescription>
              </DialogHeader>

              <div className="-mx-4 min-h-0 overflow-y-auto px-4 py-1">
                <Stack align="start" className="h-full w-full" gap={6}>
                  <FormInput
                    label="Services needed"
                    error={
                      touched.service_needed && values.service_needed
                        ? errors.service_needed
                        : undefined
                    }
                    {...getFieldProps("service_needed")}
                    required
                    fullWidth
                  />
                  <div
                    className={cn(
                      "w-full transition-[height] duration-300 ease-in-out",
                      {
                        "h-full": values.isAddingNewAdvocate,
                        "h-[56px]": !values.isAddingNewAdvocate,
                      },
                    )}
                  >
                    {!values.isAddingNewAdvocate ? (
                      <AdvocateSelector
                        businessId={
                          effectiveBusinessId || userInfo?.parentable_id
                        }
                        value={values.advocate_id}
                        onChange={(value) =>
                          setFieldValue("advocate_id", value)
                        }
                        fullWidth
                        required
                        onAddNew={() =>
                          setFieldValue("isAddingNewAdvocate", true)
                        }
                      />
                    ) : (
                      <NewAdvocateFields businessId={effectiveBusinessId} />
                    )}
                  </div>
                  <Text variant="title">Potential customer</Text>
                  <FormInput
                    label="Customer name"
                    error={
                      touched.customer_name && values.customer_name
                        ? errors.customer_name
                        : undefined
                    }
                    {...getFieldProps("customer_name")}
                    fullWidth
                  />
                  <PhoneInput
                    value={values.customer_phone}
                    onChange={(value) => setFieldValue("customer_phone", value)}
                    label="Phone number"
                    error={
                      touched.customer_phone ? errors.customer_phone : undefined
                    }
                    required
                    fullWidth
                  />
                </Stack>
              </div>

              <DialogFooter className="flex-none">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                >
                  Create
                </Button>
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      )}
    </Formik>
  );
};

export default AddReferralModal;
